import { useCallback, FC, useMemo } from 'react'
import styles from './styles.module.css'
import { useCookies } from 'react-cookie'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
  updateProfile,
  updateProfileImage
} from '../../services/profileService'
import toast from 'react-hot-toast'
import { Card } from '../../components/Card'
import { Button } from '../../components/Button'
import Plus from '../../components/svg/Plus'
import Close from '../../components/svg/Close'

interface IEditProfileProps {
  onClose: () => void
  defaultValues?: {
    nickname: string
    twitter: string
    telegram: string
  }
  onProfileUpdated?: (data: any) => void
}

export const EditProfile: FC<IEditProfileProps> = (
  props: IEditProfileProps
) => {
  const {
    onClose,
    defaultValues = {
      nickname: '',
      twitter: '',
      telegram: ''
    },
    onProfileUpdated = () => {}
  } = props

  const [cookie] = useCookies()
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues
  } as {
    defaultValues: {
      nickname: string
      twitter: string
      telegram: string
      image: File[]
    }
  })
  const watchFields = watch(['image'])
  const imageUploaded = useMemo(() => {
    return watchFields[0]?.length ? true : false
  }, [watchFields])
  const onSubmit: SubmitHandler<any> = useCallback(
    async (data: any) => {
      try {
        const tempData = {
          nickname: data.nickname,
          twitter: data.twitter,
          telegram: data.telegram
        } as any
        if (data.image[0]) {
          const { url: image_url }: { url: string } = await updateProfileImage(
            data.image[0],
            cookie.bearer
          )
          tempData.image_url = image_url
        }
        const updatedData = await updateProfile(tempData, cookie.bearer)
        toast.success('profile updated successfully')
        onProfileUpdated(updatedData)
        onClose()
        reset()
      } catch (error: any) {
        toast.error(error.message)
      }
    },
    [onClose, reset, cookie.bearer, onProfileUpdated]
  )

  return (
    <Card className={styles.profileModal}>
      <div className={styles.header}>
        <div className={styles.title}>Edit profile</div>
        <div className={styles.close} onClick={onClose}>
          <Close />
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.profileForm}>
        <label className={styles.inputContainer}>
          Nickname
          <input
            type="text"
            className={styles.input}
            placeholder="Enter your nickname"
            {...register('nickname', {
              required: false,
              validate: (value) =>
                value.length <= 32 ||
                'Nickname length must be less than or equal to 32'
            })}
          />
          {errors.nickname && (
            <span className={styles.error}>
              {errors.nickname.message?.toString()}
            </span>
          )}
        </label>
        <label className={styles.inputContainer}>
          Twitter
          <input
            type="text"
            className={styles.input}
            placeholder="Enter your twitter"
            {...register('twitter', {
              required: false
            })}
          />
          {errors.twitter && (
            <span className={styles.error}>
              {errors.twitter.message?.toString()}
            </span>
          )}
        </label>
        <label className={styles.inputContainer}>
          Telegram
          <input
            type="text"
            className={styles.input}
            placeholder="Enter your telegram"
            {...register('telegram', {
              required: false
            })}
          />
          {errors.telegram && (
            <span className={styles.error}>
              {errors.telegram.message?.toString()}
            </span>
          )}
        </label>
        <div className={styles.inputContainer}>
          Image
          <input
            type="file"
            id="file"
            className={styles.input}
            accept="image/jpeg, image/png"
            {...register('image', {
              required: false,
              validate: (value) =>
                !value[0] || value[0].size <= 10000000 || 'File size is too big'
            })}
          />
          {!imageUploaded && <div className={styles.inputFile} />}
          <label htmlFor="file" className={styles.fileLabel}>
            upload image
            <Plus style={{ width: 31, height: 31 }} />
          </label>
          {errors.image && (
            <span className={styles.error}>
              {errors.image.message?.toString()}
            </span>
          )}
        </div>
        <Button type="submit" className={styles.submitButton}>
          Submit
        </Button>
      </form>
    </Card>
  )
}
